<template>
  <div>
    <b-card no-body>
      <!-- Head -->
      <table-header>
        <template #action>
          <b-button
            v-if="canAccess('manage', 'Review')"
            variant="primary"
            :to="{ name: 'reviews-create'}"
          >
            Buat Review
          </b-button>
        </template>
      </table-header>
      <!-- Alert -->
      <!-- Body -->
      <b-skeleton-table
        v-if="isLoading"
        :rows="5"
        :columns="4"
      />
      <b-table
        v-else
        ref="refUserListTable"
        :items="reviews.data"
        responsive
        :fields="tableFields"
        primary-key="id"
        show-empty
        empty-text="Tidak ada data yang ditemukan"
        class="position-relative mh-160"
      >
        <!-- Column: Product -->
        <template #cell(product)="data">
          <ProductItem
            v-if="data.item.product"
            :img="data.item.product.images.length ? data.item.product.images[0].image_url : ''"
            :title="data.item.product.name"
            :subtitle="data.item.product.merchant ? data.item.product.merchant.name : ''"
          />
        </template>

        <!-- Column: Name -->
        <template #cell(name)="data">
          <div>{{ getNameItem(data.item) }}</div>
          <div
            v-if="data.item.source"
            class="text-muted"
          >
            {{ data.item.source }}
          </div>
        </template>

        <!-- Column: Star -->
        <template #cell(star)="data">
          <div class="d-flex align-items-center">
            <feather-icon
              icon="StarIcon"
              size="16"
              class="align-middle text-warning"
            />
            <h5 class="ml-50 mb-0">
              {{ data.item.star }}
            </h5>
          </div>
        </template>

        <!-- Column: Status -->
        <template #cell(status)="data">
          <b-badge
            v-if="data.item.is_publish"
            variant="light-success"
          >
            Publikasi
          </b-badge>
          <b-badge
            v-else
            variant="light-warning"
          >
            Privat
          </b-badge>
        </template>

        <!-- Column: Created At -->
        <template #cell(reviewed_at)="data">
          {{ formatSelectDate(data.item.reviewed_at, ['date','month','year','time']) }}
        </template>

        <!-- Column: Actions -->
        <template
          #cell(actions)="data"
        >
          <div class="text-nowrap">
            <table-action
              :actions="tableAction"
              :data="data"
            />
          </div>
        </template>

      </b-table>
      <!-- Footer -->
      <div class="mx-2 mb-2">
        <pagination
          :data="reviews.meta"
          action="review/getReviewsList"
        />
      </div>
    </b-card>

    <b-modal
      id="modal-detail"
      title="Detail Review"
      hide-footer
      @hidden="detailData = ''"
    >
      <div v-if="detailData">
        <ProductItem
          :img="detailData.product.images.length ? detailData.product.images[0].image_url : ''"
          :title="detailData.product.name"
          :subtitle="detailData.product.merchant ? detailData.product.merchant.name : ''"
        />
        <hr>
      </div>

      <div v-if="detailData.name || detailData.user">
        <div
          class="d-flex align-items-center"
        >
          <b-avatar
            variant="primary"
            :text="initialText(detailData.user ? detailData.user.name : detailData.name)"
            class="align-baseline"
            :src="linkStaging(detailData.user ? detailData.user.photo_url : null)"
          />
          <div>
            <div class="ml-1">
              {{ detailData.user ? detailData.user.name : detailData.name }}
            </div>
            <small class="ml-1 text-muted">
              {{ detailData.user ? 'Akun Miniapoly' : '' }}
            </small>
          </div>
        </div>
        <hr>
      </div>

      <div v-if="detailData">
        <div class="text-center">
          <b-form-rating
            v-model="detailData.star"
            no-border
            show-value
            inline
            variant="warning"
            readonly
          />
        </div>
        <hr>
        <table class="mt-xl-0 w-100">
          <tr>
            <th class="pb-50">
              <span class="font-weight-bold">Dibuat pada</span>
            </th>
            <td class="pb-50 text-capitalize">
              {{ formatSelectDate(detailData.reviewed_at, ['date','month','year','time']) }}
            </td>
          </tr>
          <tr>
            <th class="pb-50">
              <span class="font-weight-bold">Source</span>
            </th>
            <td class="pb-50 text-capitalize">
              <span v-if="detailData.source">
                {{ detailData.source }}
              </span>
              <span v-else>-</span>
            </td>
          </tr>
          <tr>
            <th class="pb-50">
              <span class="font-weight-bold">Publikasi</span>
            </th>
            <td class="pb-50 text-capitalize">
              <span v-if="detailData.is_publish">
                <b-badge
                  variant="light-success"
                >
                  Publikasi
                </b-badge>
              </span>
              <span v-else>
                <b-badge
                  variant="light-warning"
                >
                  Privat
                </b-badge>
              </span>
            </td>
          </tr>
        </table>
        <hr>
        <h6>Review:</h6>
        <p>{{ detailData.review ? detailData.review : '-' }}</p>
        <div>
          <hr>
          <h6>Foto{{ detailData.images.length ? ` (${detailData.images.length})` : '' }}:</h6>
          <div
            v-if="detailData.images.length"
            class="d-flex flex-wrap mt-2"
          >
            <ShowImage
              v-for="(img, index) in detailData.images"
              :key="index"
              class="mr-50 mb-50"
              :src="img.image_url"
              :index="index"
              :set-main="false"
              :action="false"
              :preview-title="`Foto review ke ${index+1}`"
            />
          </div>
          <div
            v-else
            class="text-center text-muted my-3"
          >
            Tidak ada foto review
          </div>
        </div>
      </div>
    </b-modal>

    <b-modal
      v-if="canAccess('manage', 'Review')"
      id="modal-delete"
      title="Apakah anda yakin"
      @hidden="deletedData = ''"
    >
      Ingin Menghapus Review ini?
      <template #modal-footer="{ cancel }">
        <b-button
          variant="outline-secondary"
          :disabled="isDeleting"
          @click="cancel()"
        >
          Batalkan
        </b-button>
        <b-button
          variant="danger"
          :disabled="isDeleting"
          @click.prevent="handleDelete"
        >
          <b-spinner
            v-if="isDeleting"
            small
            label="Spinning"
          />
          {{ isDeleting ? 'Menghapus' : 'Ya, Hapus' }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import {
  BSkeletonTable,
  BCard,
  BButton,
  BTable,
  BAvatar,
  BFormRating,
  BBadge,
  BSpinner,
} from 'bootstrap-vue'

import ProductItem from '@/layouts/components/custom/ProductItem.vue'
import ShowImage from '@/layouts/components/custom/ShowImage.vue'
import Pagination from '@/layouts/components/custom/Pagination.vue'
import TableHeader from '@/layouts/components/custom/TableHeader.vue'
import TableAction from '@/layouts/components/custom/TableAction.vue'

export default {
  components: {
    BSkeletonTable,
    BCard,
    BButton,
    BTable,
    BAvatar,
    BFormRating,
    BBadge,
    BSpinner,
    ProductItem,
    ShowImage,
    Pagination,
    TableHeader,
    TableAction,
  },
  data() {
    return {
      tableFields: [
        { key: 'product', label: 'Product', thStyle: { width: '25%' } },
        { key: 'name', label: 'Name' },
        { key: 'star', label: 'Star' },
        { key: 'status', label: 'Status' },
        { key: 'reviewed_at', label: 'Dibuat pada' },
        { key: 'actions', label: '', thStyle: { width: '1%' } },
      ],
      detailData: '',
      deletedData: '',
      isDeleting: false,
    }
  },
  computed: {
    ...mapState('review', ['reviews', 'isLoading']),
    query() {
      return this.$route.query
    },
    tableAction() {
      return [
        {
          access: null,
          to: null,
          action: data => { this.detailReview(data) },
          icon: 'EyeIcon',
          title: 'Detail',
        },
        {
          access: this.canAccess('manage', 'Review'),
          to: 'reviews-edit|id:id',
          action: '',
          icon: 'EditIcon',
          title: 'Edit',
        },
        {
          access: this.canAccess('manage', 'Review'),
          to: null,
          action: data => { this.deleteConfirmation(data.id) },
          icon: 'TrashIcon',
          title: 'Hapus',
        },
      ]
    },
  },
  async created() {
    await this.getReviewsList(this.query)
  },
  methods: {
    ...mapActions('review', ['getReviewsList', 'deleteReview']),
    detailReview(value) {
      this.$bvModal.show('modal-detail')
      this.detailData = value
    },
    deleteConfirmation(value) {
      this.$bvModal.show('modal-delete')
      this.deletedData = value
    },
    async handleDelete() {
      this.isDeleting = true
      await this.deleteReview({ reviewId: this.deletedData })
      this.isDeleting = false
      this.$bvModal.hide('modal-delete')
    },
    getNameItem(data) {
      const { user, name } = data
      const userName = user ? user.name : ''
      const by = userName || name

      return by
    },
  },
}
</script>
